import { Box, Stack, Typography } from "@mui/material";

interface AgeCriteria {
	is_present: boolean;
	use_specific_ages: boolean;
	specific_age_list: number[];
	min_age: number;
	max_age: number;
	use_age_bands: boolean;
	age_band_ranges: string[];
}

interface Props {
	ageCriteria?: AgeCriteria;
}

/* =========================
   Reusable Value Pill
========================= */
const ValuePill = ({ children }: { children: React.ReactNode }) => (
	<Box
		sx={{
			px: 1.35,
			py: 0.5,
			borderRadius: 1,
			backgroundColor: "#f0fdf4",
			color: "#166534",
			fontSize: 14.5,
			fontWeight: 500,
			lineHeight: 1.35,
			whiteSpace: "nowrap",
			boxShadow: "0 1px 1px rgba(0,0,0,0.05)"
		}}
	>
		{children}
	</Box>
);

/* =========================
   Reusable Section
========================= */
const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
		<Typography fontWeight={600} fontSize={13} color="text.secondary">
			{title}
		</Typography>
		{children}
	</Box>
);

/* =========================
   RuleCard
========================= */
const RuleCard = ({ title, children, ageRange }: { title: string; children?: React.ReactNode; ageRange?: string }) => (
	<Box
		sx={{
			p: 1.25,
			borderRadius: 1,
			backgroundColor: "#f9fafb",
			minWidth: 150,
			flex: 1,
			boxShadow: "0 1px 2px rgba(0,0,0,0.06)"
		}}
	>
		<Typography fontWeight={600} fontSize={14.5} gutterBottom>
			{title}
		</Typography>

		{ageRange && (
			<Typography fontSize={13} color="text.secondary">
				Age Range: <b>{ageRange}</b>
			</Typography>
		)}

		{children && <Box mt={1}>{children}</Box>}
	</Box>
);

/* =========================
   Main Component
========================= */
const AgeCriteriaCard = ({ ageCriteria }: Props) => {
	const hasData = ageCriteria?.is_present;

	if (!hasData) {
		return (
			<Typography fontSize={14} color="text.secondary">
				N/A
			</Typography>
		);
	}

	return (
		<Box
			sx={{
				p: 2,
				borderRadius: 2,
				backgroundColor: "#fefefe",
				border: "1px solid #e5e7eb",
				display: "flex",
				flexDirection: "column",
				gap: 1.5
			}}
		>
			<Typography fontWeight={600} fontSize={15}>
				Age Criteria
			</Typography>

			{/* Specific Ages */}
			<Section title="Specific Ages">
				{ageCriteria.use_specific_ages ? (
					<Stack direction="row" flexWrap="wrap" gap={0.75}>
						{ageCriteria.specific_age_list.length ? (
							ageCriteria.specific_age_list.map((age, idx) => <ValuePill key={idx}>{age}</ValuePill>)
						) : (
							<Typography fontSize={14} color="text.secondary">
								N/A
							</Typography>
						)}
					</Stack>
				) : (
					<Typography fontSize={14} color="text.secondary">
						N/A
					</Typography>
				)}
			</Section>

			{/* Age Range */}
			<Section title="Age Range">
				<RuleCard
					title="Applicable Range"
					ageRange={`${ageCriteria.min_age ?? "N/A"} – ${ageCriteria.max_age ?? "N/A"}`}
				/>
			</Section>

			{/* Age Bands */}
			<Section title="Age Bands">
				{ageCriteria.use_age_bands ? (
					<Stack direction="row" flexWrap="wrap" gap={0.75}>
						{ageCriteria.age_band_ranges.length ? (
							ageCriteria.age_band_ranges.map((range, idx) => <ValuePill key={idx}>{range}</ValuePill>)
						) : (
							<Typography fontSize={14} color="text.secondary">
								N/A
							</Typography>
						)}
					</Stack>
				) : (
					<Typography fontSize={14} color="text.secondary">
						N/A
					</Typography>
				)}
			</Section>
		</Box>
	);
};

export default AgeCriteriaCard;
